import React, { ChangeEvent } from "react";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { SelectItem } from "../../models/SelectItem";
import { Button } from "primereact/button";
import { CreateNonIbanPaymentMethodRequest, PaymentMethodStatus } from "../../models/MerchantModel";
import { BankAccountConstant } from "../../models/IncomingBankAccount";
import { utils } from "../../utils/utils";
import { currenciesApiService } from "../../services/api/merchants/CurrenciesApiService";
import { storageService } from "../../services/StorageService";
import { Check } from "lucide-react";

interface AddNonIbanProps {
    OnSuccess: (item: CreateNonIbanPaymentMethodRequest) => void;
    isSubmitting?: boolean;
    OnBack?: () => void;
}

interface AddNonIBANState {
    accountOwnerName: string;
    bankAccountName: string;
    bankName: string;
    bankAccountNumber: string;
    currency: string;
    swift: string;
    bankAddress: string;
    bankStatement: string;
    bankStatementFileName: string;
    currencies: SelectItem[];
}

export class AddNonIBAN extends React.Component<AddNonIbanProps, AddNonIBANState> {
    bankStatementInput: HTMLInputElement | null = null;

    constructor(props: AddNonIbanProps) {
        super(props);

        this.state = {
            bankAccountName: "",
            accountOwnerName: "",
            bankName: "",
            bankAccountNumber: "",
            currency: "",
            swift: "",
            bankAddress: "",
            bankStatement: "",
            bankStatementFileName: "",
            currencies: [],
        };
    }

    componentDidMount(): void {
        this.onLoad();
    }

    onLoad = async () => {
        const currenciesResult = await currenciesApiService.getMerchantActiveCurrencies(Number(storageService.getSelectedMerchantId()));

        if (currenciesResult.success) {
            const currencies = currenciesResult.data.map((a) => ({ label: a.charCode, value: a.charCode }) as SelectItem);
            currencies.unshift({ label: "NONE", value: "" });
            this.setState({ currencies: currencies });
        }
    };

    setBankStatement = async (file: File) => {
        this.setState({ bankStatementFileName: file.name || "" });
        const data = (await utils.convertFileToBase64(file)) as string;
        this.setState({ bankStatement: data });
    };

    onChangeAccountNumber = (e: ChangeEvent<HTMLInputElement>) => {
        if (!isNaN(Number(e.target.value))) {
            this.setState({ bankAccountNumber: e.target.value });
        }
    };

    onSubmit = () => {
        const obj = new CreateNonIbanPaymentMethodRequest();
        obj.bankAccountName = this.state.bankAccountName;
        obj.accountHolderName = this.state.accountOwnerName;
        obj.bankName = this.state.bankName;
        obj.bankAccountNumber = this.state.bankAccountNumber;
        obj.currency = this.state.currency;
        obj.swift = this.state.swift;
        obj.bankAddress = this.state.bankAddress;
        obj.bankStatement = this.state.bankStatement;
        obj.bankStatementFileName = this.state.bankStatementFileName;
        obj.status = PaymentMethodStatus.Approve;
        this.props.OnSuccess(obj);
    };

    render(): React.ReactNode {
        return (
            <div className="space-y-5 p-1">
                {/* Account Name + Account Owner Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">Account Name</label>
                        <InputText
                            value={this.state.bankAccountName}
                            type="text"
                            placeholder="Account Name"
                            onChange={(e) => this.setState({ bankAccountName: e.target.value })}
                            className="w-full"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">Account Owner Name</label>
                        <InputText
                            value={this.state.accountOwnerName}
                            type="text"
                            placeholder="Account Owner Name"
                            onChange={(e) => this.setState({ accountOwnerName: e.target.value })}
                            className="w-full"
                        />
                    </div>
                </div>

                {/* Bank Name + Currency */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">Bank Name</label>
                        <InputText
                            value={this.state.bankName}
                            type="text"
                            placeholder="Bank Name"
                            onChange={(e) => this.setState({ bankName: e.target.value })}
                            className="w-full"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">Currency</label>
                        <Dropdown
                            className="w-full"
                            value={this.state.currency}
                            options={this.state.currencies}
                            onChange={(e) => this.setState({ currency: e.value })}
                            placeholder="Select Currency"
                        />
                    </div>
                </div>

                {/* Account Number + Swift */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">Account Number</label>
                        <InputText
                            value={this.state.bankAccountNumber}
                            type="text"
                            inputMode="text"
                            pattern="[0-9\s]"
                            placeholder="Account Number"
                            onChange={(e) => this.onChangeAccountNumber(e)}
                            maxLength={BankAccountConstant.accountNumbeMaxLength}
                            className="w-full"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">Swift</label>
                        <InputText
                            value={this.state.swift}
                            type="text"
                            placeholder="Swift Code"
                            onChange={(e) => this.setState({ swift: e.target.value })}
                            className="w-full"
                        />
                    </div>
                </div>

                {/* Bank Address + Bank Statement */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">Bank Address</label>
                        <InputText
                            value={this.state.bankAddress}
                            type="text"
                            placeholder="Bank Address"
                            onChange={(e) => this.setState({ bankAddress: e.target.value })}
                            className="w-full"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">Bank Statement</label>
                        <InputText
                            ref={(el) => (this.bankStatementInput = el)}
                            type="file"
                            onChange={(e) => e.target.files && this.setBankStatement(e.target.files[0])}
                            className="w-full"
                        />
                    </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-2">
                    {this.props.OnBack && (
                        <Button
                            label="Back"
                            outlined
                            onClick={this.props.OnBack}
                            disabled={this.props.isSubmitting}
                        />
                    )}
                    <Button
                        label="Submit"
                        icon={<Check size={16} />}
                        loading={this.props.isSubmitting}
                        disabled={this.props.isSubmitting}
                        onClick={this.onSubmit}
                    />
                </div>
            </div>
        );
    }
}
